import { tokenise } from 'utils/filters';
import { getStringValue, isExcluded } from 'utils/search';

const isPlainObject = value =>
  value && typeof value === 'object' && !Array.isArray(value);

// Strip a single balanced pair of wrapping parentheses, e.g. `(a OR b)` -> `a OR b`.
// A field clause like `Postcode:(275*)` is left intact: its leading `(` is not the one
// closed by the trailing `)`, so stripping them independently would drop the `)`.
const stripWrappingParens = value => {
  if (!value.startsWith('(') || !value.endsWith(')')) {
    return value;
  }
  let depth = 0;
  for (let i = 0; i < value.length; i += 1) {
    if (value[i] === '(') {
      depth += 1;
    } else if (value[i] === ')') {
      depth -= 1;
      if (depth === 0) {
        return i === value.length - 1 ? value.slice(1, -1) : value;
      }
    }
  }
  return value;
};

// Spans of `Field:( ... )` groups, e.g. `TopMeshTopics:("cancer" OR "melanoma")`, so every quoted
// phrase inside the group inherits the field scope (and the group's NOT) — not just the phrase
// adjacent to the colon. Quoted spans are skipped while matching the closing paren so parens
// inside a phrase (`"tumor (malignant)"`) don't distort the depth count.
export const fieldGroupRanges = term => {
  const ranges = [];
  const re = /([A-Za-z_][\w.]*)\s*:\s*\(/g;
  let m;
  while ((m = re.exec(term)) !== null) {
    const open = m.index + m[0].length - 1;
    let depth = 0;
    let close = -1;
    for (let i = open; i < term.length; i += 1) {
      const ch = term[i];
      if (ch === '"') {
        const end = term.indexOf('"', i + 1);
        if (end === -1) {
          break;
        }
        i = end;
      } else if (ch === '(') {
        depth += 1;
      } else if (ch === ')') {
        depth -= 1;
        if (depth === 0) {
          close = i;
          break;
        }
      }
    }
    if (close !== -1) {
      ranges.push({ field: m[1], start: m.index, open, close });
    }
  }
  return ranges;
};

// Spans of plain paren groups directly preceded by NOT or a standalone `-`
// (`NOT (CRISPR OR "x")`), so the negation propagates to every phrase inside the group.
// Quote-aware for the same reason as fieldGroupRanges.
const negatedParenSpans = term => {
  const spans = [];
  const stack = [];
  for (let i = 0; i < term.length; i += 1) {
    const ch = term[i];
    if (ch === '"') {
      const end = term.indexOf('"', i + 1);
      if (end === -1) {
        break;
      }
      i = end;
    } else if (ch === '(') {
      stack.push(i);
    } else if (ch === ')') {
      const open = stack.pop();
      if (open === undefined) {
        continue;
      }
      const before = term.slice(0, open).replace(/[\s(]+$/, '');
      if (/\bNOT$/i.test(before) || /(?:^|[\s(])-$/.test(before)) {
        spans.push({ open, close: i });
      }
    }
  }
  return spans;
};

// Splits on AND/OR only at paren/bracket depth zero, so grouped clauses
// (`(Presenter:true OR Kol:true)`) stay whole while top-level siblings separate.
const splitTopLevelSegments = str => {
  const segments = [];
  let current = '';
  let depth = 0;
  for (let i = 0; i < str.length; i += 1) {
    const ch = str[i];
    if (ch === '(' || ch === '[' || ch === '{') {
      depth += 1;
    } else if (ch === ')' || ch === ']' || ch === '}') {
      depth = Math.max(0, depth - 1);
    } else if (depth === 0) {
      const op = /^(AND|OR)\b/i.exec(str.slice(i));
      const prevCh = i === 0 ? ' ' : str[i - 1];
      if (op && /[\s)\]}]/.test(prevCh)) {
        segments.push(current);
        current = '';
        i += op[1].length - 1;
        continue;
      }
    }
    current += ch;
  }
  segments.push(current);
  return segments.map(s => s.trim()).filter(Boolean);
};

/**
 * Stage-2 review chips for `term`. Detects Lucene-style NOT before a quoted phrase for red excluded styling only;
 * does not mutate `filters.term`. Complex nested NOT/AND trees may not classify every segment.
 */
export const parseTermReviewEntries = term => {
  if (typeof term !== 'string') {
    return [];
  }

  const entries = [];
  const groupRanges = fieldGroupRanges(term);
  const negSpans = negatedParenSpans(term);
  // Lucene's prohibit operator: a standalone `-` (at the start, or after whitespace/paren),
  // NOT a hyphen inside a word like `anti-"cancer"`. The generator emits NOT, but the backend
  // parser accepts `-` too, so it is honoured here for completeness.
  const STANDALONE_MINUS = /(?:^|[\s(])-$/;
  // A NOT or `-` before the group start negates everything inside it, even through wrapping
  // parens (`NOT (TopMeshTopics:("animals"))`).
  const groupNegated = r => {
    const before = term.slice(0, r.start).replace(/[\s(]+$/, '');
    return /\bNOT$/i.test(before) || STANDALONE_MINUS.test(before);
  };

  // Cleans dangling operators out of `raw`, splits it at top level (never inside
  // parens/brackets), and classifies each segment: field clauses become their own chips —
  // a simple `Field:bareword` renders scoped-style (`Field: bareword`) like quoted values,
  // complex values (comparators, ranges, paren groups) stay verbatim — while bare keywords
  // become keyword chips. Shared by the quoted-term leftovers and the no-quotes fallback.
  const pushLeftoverSegments = raw => {
    let rest = raw.replace(/\s+/g, ' ').trim();
    let prev;
    do {
      prev = rest;
      rest = rest
        .replace(/^(?:AND|OR)\b\s*/i, '') // leading AND/OR
        .replace(/\s*\b(?:AND|OR|NOT)$/i, '') // trailing dangling operator (incl. orphan NOT)
        .replace(/^NOT\s+(?=AND\b|OR\b)/i, '') // leading NOT orphaned before another operator
        .replace(/^-\s*(?=AND\b|OR\b|$)/i, '') // leading `-` whose clause was chipped and removed
        .replace(/(^|\s)-$/, '$1') // trailing orphan `-`
        .trim();
    } while (rest !== prev);

    splitTopLevelSegments(rest).forEach(segment => {
      if (/[A-Za-z_][\w.]*\s*:/.test(segment)) {
        const simple = segment.match(
          /^(NOT\s+|-)?([A-Za-z_][\w.]*)\s*:\s*([\w.-]+)$/i
        );
        if (simple) {
          entries.push({
            text: `${simple[2]}: ${simple[3]}`,
            excluded: Boolean(simple[1]),
          });
          return;
        }
        // A complex field clause (e.g. `Funding:>5000000`, `Date:[a TO b]`,
        // `(Presenter:true OR Kol:true)`) stays verbatim as one chip. A surviving leading
        // NOT or `-` genuinely negates it (orphans were stripped above), so flag it
        // excluded for red styling.
        entries.push({
          text: segment,
          excluded: /^(?:NOT\b|-)/i.test(segment),
        });
        return;
      }
      // No field clause: bare keywords the agent left unquoted, glued together by operators and
      // stray parens. Split on AND/OR so multi-word keywords stay whole; a NOT/`-` on the keyword
      // or on the segment's paren group marks it excluded. Pure operator/paren debris dissolves
      // to nothing and produces no chip.
      const segmentNegated = /^(?:NOT\b|-)\s*\(/i.test(segment);
      segment.split(/\b(?:AND|OR)\b/i).forEach(piece => {
        const cleaned = piece
          .replace(/[()]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        const excluded = segmentNegated || /^(?:NOT\b|-)/i.test(cleaned);
        const text = cleaned.replace(/^(?:NOT\b|-)\s*/i, '').trim();
        // Text that is itself just an operator is debris from degenerate nesting
        // (`NOT (NOT "a")`), never a keyword — drop it rather than chip it.
        if (text && !/^(?:NOT|AND|OR)$/i.test(text)) {
          entries.push({ text, excluded });
        }
      });
    });
  };
  // A trailing `~9` marks a Lucene proximity ("slop") phrase — surfaced on the phrase's own
  // chip as `Proximity: <phrase>` rather than as its own bare, unlabeled chip.
  const re = /"([^"]+)"(~\d+)?/g;
  let match;
  while ((match = re.exec(term)) !== null) {
    const text = match[1]?.trim();
    if (!text) {
      continue;
    }
    const proximity = match[2] ? match[2].slice(1) : null;
    const matchIndex = match.index;
    const prefix = term.slice(0, matchIndex);

    // A phrase inside a `Field:( ... )` group takes the group's field as its scope; the innermost
    // group wins if they nest. A NOT either directly before the phrase or before the group (even
    // through wrapping parens, `NOT (TopMeshTopics:("animals"))`) marks it excluded.
    const group = groupRanges
      .filter(r => r.open < matchIndex && matchIndex < r.close)
      .pop();
    // A NOT/`-` on any enclosing plain paren group (`NOT (CRISPR OR "x")`) negates the
    // phrase even though nothing negating sits directly beside it.
    const inNegatedSpan = negSpans.some(
      s => s.open < matchIndex && matchIndex < s.close
    );

    let field = null;
    let excluded = false;
    if (group) {
      field = group.field;
      excluded =
        groupNegated(group) ||
        inNegatedSpan ||
        /\bNOT$/i.test(prefix.trimEnd()) ||
        STANDALONE_MINUS.test(prefix);
    } else {
      // Optional `Field:` scope (incl. dotted names) immediately before the quote, e.g. `TopMeshTopics:`.
      const fieldMatch = prefix.match(/([A-Za-z_][\w.]*)\s*:\s*$/);
      field = fieldMatch ? fieldMatch[1] : null;
      // The text before the field prefix (or before the quote when unscoped) carries the NOT/`-`,
      // since a field scope sits between the negation and the quote (`NOT TopMeshTopics:"animals"`,
      // `-title:"cancer"`).
      const beforeField = field ? prefix.slice(0, fieldMatch.index) : prefix;
      excluded =
        inNegatedSpan ||
        /\bNOT$/i.test(beforeField.trimEnd()) ||
        STANDALONE_MINUS.test(beforeField);
    }

    const label = field ? `${field}: ${text}` : text;
    entries.push({
      text: proximity ? `Proximity: ${label}` : label,
      excluded,
      // `phrase`/`proximity` let the UI build a tooltip explaining the `~N` value without
      // re-parsing it back out of the display text.
      ...(proximity ? { proximity, phrase: label } : {}),
    });
  }

  // A bare (unquoted) value sharing a group with quoted phrases — `TopMeshTopics:("cancer" OR
  // mice)` — gets a scoped chip like its quoted siblings. A NOT directly before the bare value
  // (or on the whole group) marks it excluded. Fully-unquoted groups (`Postcode:(275*)`) are
  // left for the verbatim field-clause path below.
  groupRanges.forEach(r => {
    const inner = term.slice(r.open + 1, r.close);
    if (!inner.includes('"')) {
      return;
    }
    const bare = inner.replace(/"[^"]+"(?:~\d+)?/g, ' ');
    let pendingNot = false;
    bare.split(/\s+/).forEach(token => {
      let word = token.replace(/[()]/g, '');
      if (!word) {
        return;
      }
      if (/^(AND|OR)$/i.test(word)) {
        pendingNot = false;
        return;
      }
      if (/^NOT$/i.test(word) || word === '-') {
        pendingNot = true;
        return;
      }
      const minusNegated = word.startsWith('-');
      word = word.replace(/^-+/, '');
      if (!word) {
        return;
      }
      entries.push({
        text: `${r.field}: ${word}`,
        excluded:
          pendingNot ||
          minusNegated ||
          groupNegated(r) ||
          negSpans.some(s => s.open < r.open && r.close <= s.close),
      });
      pendingNot = false;
    });
  });

  if (entries.length > 0) {
    // Surface any Lucene field clauses (e.g. `Field:value`) that the agent embedded in the term
    // alongside the quoted keywords, so an applied constraint is never silently hidden. Position-
    // independent: removes each quoted phrase together with any `Field:` scope that prefixes it (so a
    // field-scoped keyword like `TopMeshTopics:"crispr"` leaves no orphaned `TopMeshTopics:`), then
    // cleans up the boolean operators left dangling where a phrase used to be.
    //
    // Quote-bearing field groups are already fully represented as chips (quoted phrases and bare
    // values alike), so their whole span is spliced out first — otherwise the emptied
    // `Field:( ... )` husk would surface as a bogus field-clause chip.
    let restSource = '';
    let sliceFrom = 0;
    groupRanges.forEach(r => {
      if (r.start < sliceFrom) {
        return; // nested inside a group already spliced out
      }
      if (!term.slice(r.open, r.close).includes('"')) {
        return;
      }
      restSource += term.slice(sliceFrom, r.start);
      sliceFrom = r.close + 1;
    });
    restSource += term.slice(sliceFrom);

    pushLeftoverSegments(
      restSource
        .replace(/(?:[A-Za-z_][\w.]*\s*:\s*)?"[^"]+"(?:~\d+)?/g, ' ')
        // Belt-and-braces for malformed input the splice can't cover (e.g. an unterminated quote):
        // dissolve any field whose paren group holds only whitespace/operators; a group with real
        // unquoted content (e.g. `Postcode:(275*)`) is untouched and still surfaces verbatim below.
        .replace(/[A-Za-z_][\w.]*\s*:\s*\(\s*(?:(?:AND|OR|NOT)\s*)*\)/gi, ' ')
    );

    return entries;
  }

  const fallbackTerm = stripWrappingParens(term.trim()).trim();

  // A term with no quoted phrases at all can still be pure Lucene field clauses — the
  // raw-Lucene passthrough path (`TopMeshTopics:cancer AND FundingSource:NIH`) reaches here
  // because the agent echoes typed Lucene without quoting the values. Route it through the
  // same segment pipeline so each clause gets its own chip; genuine free text (no field
  // clause) keeps the verbatim single-chip fallback.
  if (/[A-Za-z_][\w.]*\s*:/.test(fallbackTerm)) {
    pushLeftoverSegments(fallbackTerm);
    if (entries.length > 0) {
      return entries;
    }
  }

  return fallbackTerm ? [{ text: fallbackTerm, excluded: false }] : [];
};

const normaliseTokenValue = (value, fieldLabel) => {
  if (typeof value !== 'string') {
    return String(value ?? '');
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (fieldLabel && trimmed === fieldLabel) {
    return 'Yes';
  }

  return trimmed;
};

const reviewChipKey = entry => {
  if (
    typeof entry === 'object' &&
    entry !== null &&
    typeof entry.text === 'string'
  ) {
    return `${entry.excluded ? 'x' : 'i'}:${entry.text}`;
  }
  return `i:${String(entry)}`;
};

/**
 * @param {unknown} raw
 * @param {string} field
 * @returns {{ text: string, excluded: boolean } | null}
 */
const reviewEntryFromTokenValue = (raw, field) => {
  const excluded = isExcluded(raw);
  let displayRaw = raw;

  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    const rest = { ...raw };
    delete rest.isExcluded;
    displayRaw =
      rest.displayName ?? rest.label ?? rest.value ?? rest.stateName ?? '';
  } else if (excluded && typeof raw === 'string') {
    displayRaw = getStringValue(raw);
  }

  const text = normaliseTokenValue(displayRaw, field);
  if (!text) {
    return null;
  }
  return { text, excluded };
};

export const buildReviewRows = (filters, filterMeta) => {
  if (!isPlainObject(filters)) {
    return { totalCount: 0, sections: [] };
  }

  const sections = {};
  const sectionOrder = [];
  let totalCount = 0;

  const upsertRow = (sectionTitle, rowKey, field, entries) => {
    if (entries.length === 0) {
      return;
    }

    if (!sections[sectionTitle]) {
      sections[sectionTitle] = [];
      sectionOrder.push(sectionTitle);
    }

    const existingRow = sections[sectionTitle].find(row => row.key === rowKey);
    if (existingRow) {
      entries.forEach(entry => {
        const key = reviewChipKey(entry);
        if (!existingRow.values.some(v => reviewChipKey(v) === key)) {
          existingRow.values.push(entry);
        }
      });
      return;
    }

    sections[sectionTitle].push({
      key: rowKey,
      field,
      values: entries,
    });
    totalCount += 1;
  };

  const termEntries = parseTermReviewEntries(filters.term);
  if (termEntries.length > 0) {
    upsertRow('Search', 'term', 'Term', termEntries);
  }

  const tokens = tokenise(filters, filterMeta || {}, []);
  tokens.forEach(token => {
    const field = token?.metaData?.title || token.key;
    const sectionTitle = token?.metaData?.sectionTitle || 'Other';
    const entry = reviewEntryFromTokenValue(token.value, field);
    if (!entry) {
      return;
    }

    const rowKey = token.filterGroup
      ? `${token.filterGroup}.${token.key}`
      : token.key;

    upsertRow(sectionTitle, rowKey, field, [entry]);
  });

  return {
    totalCount,
    sections: sectionOrder.map(sectionTitle => ({
      sectionTitle,
      rows: sections[sectionTitle],
    })),
  };
};
